import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { IMPROVE_VALID_DATA_RATE_GUIDE_URLS } from 'app/core-lib/data-rate-widget/data-rate-widget.constants';
import { Hotel } from 'app/shared/model/hotel';
import { UtilService } from 'app/shared/util.service';
import { TranslateService } from '@ngx-translate/core';
import { ModalService } from 'app/core-lib/dialogs/modal/modal.service';
import { CoreLibEmailRateModalComponent } from 'app/core-lib/dialogs/email-rate-modal/email-rate-modal.component';
import { FollowupPropertiesService } from 'app/inter-chain/followup/properties/followup-properties.service';
import { canLoadData, getRequestParams, getQualification } from '../followup-summary-widgets.utils';
import { Qualification } from '../followup-summary-widgets.utils';
import { getSimpleCell, getCellWithTooltip, getCellWithPercentage } from '../followup-summary-widgets-table.utils';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'mh-surveys-summary-valid-data-rate',
  templateUrl: './valid-data-rate-widget.component.html',
  standalone: false,
})
export class ValidDataRateWidgetComponent implements OnChanges {
  @Input() chainFilter: Hotel[] = [];
  @Input() productId = 1;
  @Input() currentDates: any;
  @Input() currentFilterDate: any;
  @Input() surveysReady = false;
  @Input() chain: Hotel[] = [];
  @Input() isHotel = true;

  @Output() loadingChange = new EventEmitter<boolean>();

  loading = false;
  coverageValidDataRate = 0;
  coverageValidDataRateEmail = 0;
  coverageValidDataRatePhone = 0;
  qualificationValidDataRate: Qualification = 'excellent';
  reportValidDataRate = null;
  reportValidDataRateSlug = 'interchain_followup_summary_data_rate';
  pmsData: any = null;
  heigth = '100%';

  constructor(
    private utilService: UtilService,
    private followupPropertiesService: FollowupPropertiesService,
    private translateService: TranslateService,
    private modalService: ModalService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes.chainFilter || changes.currentDates || changes.surveysReady) &&
      canLoadData(this.utilService, this.chainFilter, this.surveysReady)
    ) {
      this.loadData();
    }
  }

  loadData(): void {
    this.loading = true;
    this.loadingChange.emit(true);
    const { dates, surveyIds, customerIds } = getRequestParams(this.utilService, this.chainFilter, this.currentDates);

    if (surveyIds.length > 0) {
      this.followupPropertiesService
        .getPMS(dates, customerIds, this.productId, surveyIds)
        .pipe(
          finalize(() => {
            this.loading = false;
            this.loadingChange.emit(false);
          }),
        )
        .subscribe({
          next: (response: any) => this.handleSuccess(response),
          error: () => this.handleError(),
        });
    } else {
      this.handleError();
      this.loading = false;
      this.loadingChange.emit(false);
    }
  }

  private handleSuccess(response: any): void {
    const data = response.average;
    this.pmsData = data;

    this.coverageValidDataRate = data?.email_rate?.actual || 0;

    const totalGuests = data?.total_guests?.actual || 1;
    const validEmails = data?.valid_emails?.actual || 0;
    const validPhones = data?.valid_phones?.actual || 0;

    this.coverageValidDataRateEmail = totalGuests > 0 ? (validEmails / totalGuests) * 100 : 0;
    this.coverageValidDataRatePhone = totalGuests > 0 ? (validPhones / totalGuests) * 100 : 0;

    this.qualificationValidDataRate = getQualification(this.coverageValidDataRate);
  }

  private handleError(): void {
    this.coverageValidDataRate = 0;
    this.coverageValidDataRateEmail = 0;
    this.coverageValidDataRatePhone = 0;
    this.qualificationValidDataRate = 'bad';
  }

  openImproveValidDataRateGuide(): void {
    const lang = this.utilService.currentLanguage || 'es';
    const url = IMPROVE_VALID_DATA_RATE_GUIDE_URLS[lang] ?? IMPROVE_VALID_DATA_RATE_GUIDE_URLS.es;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  onViewMore(): void {
    if (!this.pmsData) {
      return;
    }
    this.openModal();
  }

  private openModal(): void {
    const config = {
      type: 'valid-data-rate',
      data: {
        classes: {
          state: this.qualificationValidDataRate,
        },
        tooltipTranslate: { guests: 'commons.guests', pms: 'PMS', checkout: 'commons.checkout' },
        currentFilter: this.currentFilterDate,
        pms: this.pmsData,
        hotelName: this.chain[0]?.chain_brand || '',
        productId: this.productId,
        tableRows: this.buildTableRows(),
        tableHeaders: this.buildTableHeaders(),
        customImproveText: 'followup.summary.emails-pms.modal.text_improve_email',
        customTitle: 'followup.summary.emails-pms.title',
        coverage: this.coverageValidDataRate,
        customDescription: this.translateService.instant('followup.summary.emails-pms.modal.valid_guests', {
          guests: this.translateService.instant('commons.guests'),
        }),
        tooltipDescription: this.translateService.instant('followup.summary.emails-pms.tooltips.modal_qualify'),
        showWarnMessage: true,
      },
    };

    this.utilService.ga('', 'interchain-followup-datawidget-detail');
    const classes = ['no-padding', 'overlay-panel', 'lg', 'fit-height-content'];
    this.modalService
      .open(CoreLibEmailRateModalComponent, config, classes)
      .afterClosed()
      .subscribe(() => {
        this.utilService.ga('', 'interchain-followup-datamodal-close');
      });
  }

  private buildTableHeaders(): any[] {
    return [
      {
        translateKey: this.currentFilterDate?.label || '',
        cssClass: 'tw-py-2 tw-border-b tw-font-medium tw-text-gray-700',
        key: 'col1',
      },
      {
        translateKey: 'followup.summary.emails-pms.modal.total_guests',
        cssClass: 'tw-py-2 tw-border-b tw-font-medium tw-text-gray-700 tw-text-center',
        key: 'col2',
      },
      {
        translateKey: 'followup.summary.emails-pms.modal.actual',
        cssClass: 'tw-py-2 tw-border-b tw-font-medium tw-text-gray-700 tw-text-center',
        key: 'col3',
      },
      {
        translateKey: 'followup.summary.emails-pms.modal.valid',
        cssClass: 'tw-py-2 tw-border-b tw-font-medium tw-text-gray-700 tw-text-center',
        key: 'col4',
      },
    ];
  }

  private buildTableRows(): any[] {
    const totalGuests = this.pmsData.total_guests?.actual || 0;
    const totalEmails = this.pmsData.total_emails?.actual || 0;
    const validEmails = this.pmsData.valid_emails?.actual || 0;
    const totalPhones = this.pmsData.total_phones?.actual || 0;
    const validPhones = this.pmsData.valid_phones?.actual || 0;

    const emailPercentage = totalGuests > 0 ? (validEmails / totalGuests) * 100 : 0;
    const phonePercentage = totalGuests > 0 ? (validPhones / totalGuests) * 100 : 0;

    const guestsParam = { guests: this.translateService.instant('commons.guests') };

    return [
      {
        id: 'emails',
        translateKey: 'followup.summary.emails-pms.modal.with_email',
        cssClass: '',
        cells: {
          col1: getSimpleCell('followup.summary.emails-pms.modal.with_email'),
          col2: getSimpleCell(totalGuests, 'tw-text-center'),
          col3: getCellWithTooltip(totalEmails, 'followup.summary.emails-pms.tooltips.total_emails', guestsParam),
          col4: getCellWithPercentage(
            validEmails,
            emailPercentage,
            'followup.summary.emails-pms.tooltips.valid_total_emails',
            guestsParam,
          ),
        },
      },
      {
        id: 'phones',
        translateKey: 'followup.summary.emails-pms.modal.with_mobile_phone',
        cssClass: '',
        cells: {
          col1: getSimpleCell('followup.summary.emails-pms.modal.with_mobile_phone'),
          col2: getSimpleCell(totalGuests, 'tw-text-center'),
          col3: getCellWithTooltip(totalPhones, 'followup.summary.emails-pms.tooltips.total_phones', guestsParam),
          col4: getCellWithPercentage(
            validPhones,
            phonePercentage,
            'followup.summary.emails-pms.tooltips.valid_total_phones',
            guestsParam,
          ),
        },
      },
    ];
  }
}
